import { fetchEventSource } from '@microsoft/fetch-event-source';

import { SessionError } from '../auth/errors';
import { msalInstance } from '../auth/msalConfig';
import { endSession } from '../auth/session';
import { getIdToken } from '../auth/token';
import { API_BASE } from '../config';
import type {
  CardStreamProgressDto,
  CardStreamThrottledDto,
} from './generated/types.gen';
import {
  zCardStreamMessageDto,
  zCardStreamProgressDto,
  zCardStreamThrottledDto,
} from './generated/zod.gen';

export type CardProgress = CardStreamProgressDto;
export type CardThrottled = CardStreamThrottledDto;

/**
 * A live stream that can be torn down. Replaces the raw `EventSource` handle
 * callers used to hold; `close()` aborts the underlying fetch.
 */
export interface SseHandle {
  close: () => void;
}

interface CardStreamOpts {
  path: string;
  esRef: { current: SseHandle | null };
  onProgress?: (data: CardProgress) => void;
  /** An upstream rate limit paused the batch — informational, stream stays open. */
  onThrottled?: (data: CardThrottled) => void;
  /** Every card is rendered; the batch is assembling the zip/pdf. */
  onFinalizing?: () => void;
  onDone: () => void;
  onError: (message: string) => void;
  /** The server accepted the stream — it may still be silent for a while. */
  onOpen?: () => void;
  /** A reconnect is scheduled (transient drop) — the stream is not dead yet. */
  onReconnecting?: () => void;
  /** Aborting it cancels the stream, including an attempt still awaiting its token. */
  signal?: AbortSignal;
}

/** A response the server actually answered (403/404/…): retrying won't help. */
class FatalStreamError extends Error {}

/**
 * The one answered refusal worth another attempt: the token was rejected.
 *
 * Not a blip, so reconnecting with the same bearer is pointless — the retry
 * has to carry a freshly minted one, which is what `forceToken` is for. The
 * plain fetch helpers do the same thing one layer down (`auth/session.ts`);
 * a stream cannot reuse that because its request never returns.
 */
class UnauthorizedStreamError extends Error {}

/** JSON.parse that yields undefined instead of throwing on garbage. */
function safeJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

/** Consecutive failed reconnects tolerated before giving up with onError. */
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_BASE_DELAY_MS = 1_000;

/**
 * Open a card batch progress stream and wire the shared `progress`/`done`/
 * `error` handling both generators need.
 *
 * Uses `fetchEventSource` rather than the native `EventSource` so the Entra
 * token travels in the `Authorization` header instead of a query param. The
 * handle is stored on `esRef` and cleared on `done`, on a terminal `error`
 * event, and on a fatal connection error. Callers supply what to do on each
 * outcome.
 *
 * Transient disconnects (an API restart, a proxy reset, laptop sleep, the
 * server's own stream-lifetime cap) reconnect with backoff and a fresh token
 * instead of dying: the server replays progress, results and throttle state
 * on every subscribe, so a reconnect is lossless. Only a real HTTP error
 * response (403/404/…) or {@link MAX_RECONNECT_ATTEMPTS} consecutive failures
 * surface as `onError`.
 *
 * Resolves once the connection has been initiated; connection problems are
 * reported through `onError`, never as a rejection.
 */
export function openCardStream(opts: CardStreamOpts): Promise<void> {
  return openStreamAttempt(opts, 0);
}

async function openStreamAttempt(
  opts: CardStreamOpts,
  attempt: number,
  /** Renew the token before connecting — set after the server refused one. */
  forceToken = false,
): Promise<void> {
  let token: string;
  try {
    token = await getIdToken(msalInstance, { forceRefresh: forceToken });
  } catch (err) {
    // Torn down while the token was pending: a late failure belongs to a
    // stream nobody watches any more (after a job switch, to the new one).
    if (opts.signal?.aborted) {
      return;
    }
    // A dropped connection or a token-endpoint 5xx — a laptop waking before
    // its Wi-Fi — is retryable (see getIdToken); only a dead session is not.
    const next = attempt + 1;
    if (!(err instanceof SessionError) && next <= MAX_RECONNECT_ATTEMPTS) {
      // No connection exists yet, so esRef gets a handle that cancels the
      // pending retry — callers close through it.
      let closed = false;
      opts.esRef.current = { close: () => (closed = true) };
      opts.onReconnecting?.();
      setTimeout(() => {
        if (closed || opts.signal?.aborted) {
          return;
        }
        void openStreamAttempt(opts, next, forceToken);
      }, RECONNECT_BASE_DELAY_MS * next);
      return;
    }
    opts.esRef.current = null;
    opts.onError('Authentication error');
    return;
  }
  // Torn down while the token was pending (unmount, job switch): the caller
  // already closed whatever esRef held, so opening now would leak a stream.
  if (opts.signal?.aborted) {
    return;
  }

  const ctrl = new AbortController();
  let finished = false;
  let closedByUser = false;
  // A connection that actually delivered an open stream resets the failure
  // budget — only *consecutive* failed reconnects count toward giving up.
  let opened = false;

  const handle: SseHandle = {
    close: () => {
      closedByUser = true;
      finished = true;
      ctrl.abort();
    },
  };
  opts.esRef.current = handle;
  opts.signal?.addEventListener('abort', handle.close, { once: true });

  const finish = () => {
    opts.signal?.removeEventListener('abort', handle.close);
    finished = true;
    ctrl.abort();
    if (opts.esRef.current === handle) {
      opts.esRef.current = null;
    }
  };

  // Tear this connection down and open a fresh one (new token) after a delay.
  // The stale handle stays on esRef so the user can still cancel while the
  // retry timer runs; the new attempt replaces it.
  const reconnect = (nextAttempt: number, delayMs: number, force = false) => {
    opts.onReconnecting?.();
    opts.signal?.removeEventListener('abort', handle.close);
    finished = true;
    ctrl.abort();
    setTimeout(() => {
      if (closedByUser || opts.signal?.aborted) {
        return;
      }
      void openStreamAttempt(opts, nextAttempt, force);
    }, delayMs);
  };

  void fetchEventSource(`${API_BASE}${opts.path}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal: ctrl.signal,
    // The native EventSource never paused on tab-hide; keep that behaviour.
    openWhenHidden: true,
    onopen(res) {
      if (res.ok) {
        opened = true;
        opts.onOpen?.();
        return Promise.resolve();
      }
      // The server answered — this is a real refusal, not a blip.
      if (res.status === 401) {
        throw new UnauthorizedStreamError();
      }
      throw new FatalStreamError(`HTTP ${res.status}`);
    },
    onmessage(msg) {
      if (finished) {
        return;
      }
      if (msg.event === 'progress') {
        // safeParse: one malformed event must be skipped, not kill the stream
        // (a parse throw here would tear the connection down and reconnect
        // straight into the same replayed event, looping forever).
        const parsed = zCardStreamProgressDto.safeParse(safeJson(msg.data));
        if (parsed.success) {
          opts.onProgress?.(parsed.data);
        }
      } else if (msg.event === 'throttled') {
        const parsed = zCardStreamThrottledDto.safeParse(safeJson(msg.data));
        if (parsed.success) {
          opts.onThrottled?.(parsed.data);
        }
      } else if (msg.event === 'finalizing') {
        opts.onFinalizing?.();
      } else if (msg.event === 'timeout') {
        // The server caps a stream's lifetime; a batch stalled on upstream
        // rate limits can legitimately outlive it. Reconnect immediately —
        // if the batch finished meanwhile, the server replays the results
        // and closes with `done`.
        reconnect(0, 0);
      } else if (msg.event === 'done') {
        finish();
        opts.onDone();
      } else if (msg.event === 'error') {
        finish();
        let message = 'Connection error';
        try {
          message = zCardStreamMessageDto.parse(JSON.parse(msg.data)).message;
        } catch {
          /* keep the generic message */
        }
        opts.onError(message);
      }
    },
    onclose() {
      // Server closed without a terminal event (restart, proxy reset) — hand
      // control to the catch below, which reconnects.
      throw new Error('stream closed');
    },
    onerror(err) {
      // Own the retry policy; re-throw so the library never retries itself
      // (its built-in retry would reuse the captured — possibly expired — token).
      throw err;
    },
  }).catch((err: unknown) => {
    if (closedByUser || finished) {
      return;
    }
    if (err instanceof UnauthorizedStreamError) {
      // Once with a renewed token — the cached one had gone stale under a tab
      // left open on a running batch, which is the ordinary way to get here.
      if (!forceToken) {
        reconnect(attempt, 0, true);
        return;
      }
      // And it was refused again. The session is over, so say so where the
      // operator can do something about it rather than leaving a stream that
      // will never open beside a batch that is still running.
      finish();
      endSession();
      opts.onError('Authentication error');
      return;
    }
    if (err instanceof FatalStreamError) {
      finish();
      opts.onError(err.message);
      return;
    }
    const nextAttempt = opened ? 1 : attempt + 1;
    if (nextAttempt > MAX_RECONNECT_ATTEMPTS) {
      finish();
      opts.onError('Connection error');
      return;
    }
    reconnect(nextAttempt, RECONNECT_BASE_DELAY_MS * nextAttempt);
  });
}
